/* ============================================================================
   jd-jewelry-grid — behaviour for sections/jd-jewelry-grid.liquid
   ----------------------------------------------------------------------------
   Moved out of the section's inline <script> for the same reason as the CSS: it
   shipped ~37 KB into every page view of the 13 templates using this section and
   could never be cached. As an asset it is fetched once and reused.

   The tag that loads this is deliberately NOT deferred and sits exactly where the
   inline script did — after the grid markup, before the rest of the page. That
   preserves the original execution timing: the grid is already in the DOM, and on
   mobile the swipe feed replaces it as early as it did before. Adding defer would
   push that swap past the full parse and let the desktop grid flash into view on
   phones first.

   The three values that depend on Liquid are read from data attributes on
   .jjg-wrap: data-jjg-sid, data-jjg-bg, data-jjg-total.
   ============================================================================ */
(function () {
  // Values the asset cannot interpolate — read off .jjg-wrap instead. No wrap means no
  // collection is selected in the theme editor, so there is nothing to wire up.
  const WRAP = document.querySelector('.jjg-wrap');
  if (!WRAP) return;
  const SID = WRAP.dataset.jjgSid || '';
  // Decided ONCE at load: mobile gets the full-screen swipe feed instead of the grid —
  // unless the section is set to "Mobile layout: Grid", where the phone keeps the grid
  // (card carousels, wishlist and the load-more sentinel all stay wired up as on desktop).
  const IS_MOBILE = window.matchMedia('(max-width: 749px)').matches
                 && WRAP.dataset.jjgMobile !== 'grid';

  /* ── CLIENT FILTER (prototype) ────────────────────
     window.JDF is published by assets/jd-filter-client.js only while Search & Discovery
     does not serve the metafield filters, and is active only when the URL carries one.
     Non-matching cards are removed before anything is wired, so carousels, wishlist and
     the mobile feed only ever see the kept ones. Pages fetched later go through the same
     jdfKeep() — see INFINITE SCROLL and the feed's maybePaginate().
     JDF_MASTER keeps every card in server order, the removed ones included, so a later
     tick in the panel can re-filter in place — see IN-PLACE FILTERING. */
  const JDF_LIVE = window.JDF || null;                          // prototype mode at all
  const JDF = JDF_LIVE && JDF_LIVE.active ? JDF_LIVE : null;    // ...with a filter on at load
  const jdfKeep = item => !JDF_LIVE || JDF_LIVE.match(item.dataset.jjgPid);
  const JDF_MASTER = JDF_LIVE ? Array.from(document.getElementById('jjg-grid-' + SID).children) : null;
  if (JDF) WRAP.querySelectorAll('.jjg-item').forEach(item => { if (!jdfKeep(item)) item.remove(); });

  /* ── LARGE TITLE FONT MIX ─────────────────────────
     Wrap each letter of a large title in its own <span> and randomly assign
     either Dark Horse Expanded or Dark Horse Condensed, so the two widths
     alternate per character. Spaces stay as real text nodes (keep word-wrap).
     Idempotent via data-dh-mixed so re-renders don't double-wrap. */
  const DH_FONTS = ['DarkHorseExpanded', 'DarkHorseCondensed'];
  function mixDarkHorse(el) {
    if (!el || el.dataset.dhMixed === '1') return;
    const txt = el.textContent;
    if (!txt) return;
    el.textContent = '';
    for (const ch of txt) {
      if (ch === ' ' || ch === '\n' || ch === '\t') {
        el.appendChild(document.createTextNode(ch));
        continue;
      }
      const s = document.createElement('span');
      s.textContent = ch;
      s.style.fontFamily = DH_FONTS[Math.random() < 0.5 ? 0 : 1];
      el.appendChild(s);
    }
    el.dataset.dhMixed = '1';
  }

  /* ── CAROUSEL ─────────────────────────────────── */
  function initCarousel(card) {
    // Idempotent: in-place filtering puts cards back that may already have been wired, and
    // a second pass would clone the edge slides again.
    if (card._jjgCarousel) return;
    card._jjgCarousel = true;
    const track      = card.querySelector('.jjg-track');
    const origSlides = Array.from(card.querySelectorAll('.jjg-slide'));
    const dots       = Array.from(card.querySelectorAll('.jjg-dot'));
    const prevBtn    = card.querySelector('.jjg-arrow.prev');
    const nextBtn    = card.querySelector('.jjg-arrow.next');

    if (!track || origSlides.length <= 1) return;

    const total = origSlides.length;
    let current = 1;
    let animating = false;
    let carouselActive = false;

    const lastClone  = origSlides[total - 1].cloneNode(true);
    const firstClone = origSlides[0].cloneNode(true);
    lastClone.setAttribute('aria-hidden', 'true');
    firstClone.setAttribute('aria-hidden', 'true');
    track.insertBefore(lastClone, origSlides[0]);
    track.appendChild(firstClone);

    const allSlides = Array.from(track.querySelectorAll('.jjg-slide'));

    allSlides.forEach(s => {
      const img = s.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    });

    // Percentage-based sizing/positioning — NOT px. The card's real width is
    // fractional (grid columns), so a px width from Math.round left a sub-pixel
    // sliver of the NEXT slide visible at the card's right edge. The track is
    // absolutely inset:0 (width = container, fraction included); slides at 100%
    // and a translate in % of the track's own width are exact at every index.
    function setSizes() {
      track.style.width = '';
      allSlides.forEach(s => { s.style.width = '100%'; s.style.minWidth = '0'; });
    }

    function setPos(animate) {
      track.classList.toggle('animated', animate);
      track.style.transform = 'translate3d(-' + (current * 100) + '%, 0, 0)';
      const real = Math.min(Math.max(current - 1, 0), total - 1);
      dots.forEach((d, i) => d.classList.toggle('active', i === real));
    }

    setSizes();
    setPos(false);

    track.addEventListener('transitionend', () => {
      if (current === 0) { current = total; setPos(false); }
      else if (current === total + 1) { current = 1; setPos(false); }
      animating = false;
    });

    new ResizeObserver(() => { setSizes(); setPos(false); }).observe(track.parentElement);

    function go(dir) {
      if (animating) return;
      animating = true;
      current += dir;
      setPos(true);
    }

    function goTo(idx) {
      if (animating) return;
      animating = true;
      current = idx + 1;
      setPos(true);
    }

    function activateCarousel() {
      if (!carouselActive) { carouselActive = true; card.classList.add('carousel-on'); }
    }
    function deactivateCarousel() { carouselActive = false; card.classList.remove('carousel-on'); }

    if (prevBtn) prevBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); activateCarousel(); go(-1); });
    if (nextBtn) nextBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); activateCarousel(); go(1); });

    dots.forEach(dot => {
      dot.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        activateCarousel();
        goTo(parseInt(dot.dataset.index));
      });
    });

    card.closest('.jjg-item').addEventListener('mouseleave', () => {
      if (!carouselActive) return;
      const overlay = origSlides[0].cloneNode(true);
      overlay.style.cssText = 'position:absolute;inset:0;width:100%;opacity:0;z-index:1;transition:opacity 1s ease;pointer-events:none;';
      track.parentElement.appendChild(overlay);
      setTimeout(() => { overlay.style.opacity = '1'; }, 10);
      setTimeout(() => {
        current = 1; setPos(false); animating = false;
        deactivateCarousel(); overlay.remove();
      }, 1000);
    });

    let tx = 0, ty = 0, tt = 0, swipeDir = null;
    card.addEventListener('touchstart', e => {
      tx = e.touches[0].clientX; ty = e.touches[0].clientY; tt = Date.now(); swipeDir = null;
    }, { passive: true });
    card.addEventListener('touchmove', e => {
      if (swipeDir === 'v') return;
      const dx = Math.abs(e.touches[0].clientX - tx), dy = Math.abs(e.touches[0].clientY - ty);
      if (!swipeDir && (dx > 5 || dy > 5)) swipeDir = dx >= dy ? 'h' : 'v';
      if (swipeDir === 'h') e.preventDefault();
    }, { passive: false });
    card.addEventListener('touchend', e => {
      if (swipeDir !== 'h') return;
      const dx = e.changedTouches[0].clientX - tx;
      if (Math.abs(dx) > 30 || Math.abs(dx) / (Date.now() - tt) > 0.3) {
        activateCarousel(); go(dx > 0 ? -1 : 1);
      }
      swipeDir = null;
    }, { passive: true });
  }

  // Desktop only — on mobile the grid is replaced by the swipe feed (below).
  if (!IS_MOBILE) {
    document.querySelectorAll('.jjg-wrap [data-jjg-carousel]').forEach(initCarousel);
  }

  /* ── WISHLIST ─────────────────────────────────── */
  const WL_KEY = 'jd_wishlist';
  function wlGetList() { try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch { return []; } }
  function wlSave(l) { localStorage.setItem(WL_KEY, JSON.stringify(l)); }
  function wlMatchId(e, id) { return typeof e === 'string' ? e === id : e && e.id === id; }
  function wlToggle(id, handle) {
    const l = wlGetList(), idx = l.findIndex(e => wlMatchId(e, id));
    if (idx === -1) { l.push({ id, handle }); wlSave(l); return true; }
    l.splice(idx, 1); wlSave(l); return false;
  }

  function initWishlistBtn(btn) {
    if (btn.dataset.wishlistInit) return;
    btn.dataset.wishlistInit = '1';
    if (wlGetList().some(e => wlMatchId(e, btn.dataset.productId))) btn.classList.add('active');
    btn.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      const pid = btn.dataset.productId;
      const added = wlToggle(pid, btn.dataset.productHandle);
      document.querySelectorAll('[data-product-id="' + pid + '"][class*="wishlist-btn"]').forEach(b => {
        b.classList.toggle('active', added);
        if (b.className.indexOf('ai-wishlist-btn-') !== -1) b.textContent = added ? '♥' : '♡';
      });
      btn.classList.remove('just-toggled');
      void btn.offsetWidth;
      btn.classList.add('just-toggled');
      btn.addEventListener('animationend', () => btn.classList.remove('just-toggled'), { once: true });
    });
  }

  document.querySelectorAll('.jjg-wrap .pc-wishlist-btn').forEach(initWishlistBtn);

  // Desktop grid: large titles use DarkHorseExpanded only (no per-letter mix).
  // The per-letter Expanded/Condensed mix is kept for the mobile feed only.

  /* ── MOBILE SWIPE FEED (explore-all lookbook) ─────
     One full-screen product per view, vertical scroll-snap + parallax.
     Built as a body-level fixed panel (avoids the fixed-transform-trap and the
     body-grid clamp — same proven pattern as the interactive gallery). The grid
     is only hidden AFTER the panel is built, so JS failure degrades to the grid. */
  if (IS_MOBILE) initMobileFeed();
  else initRowFirst();

  /* ── LEFT-COLUMN TEXT INDENT ───────────────────────
     Mark the first .jjg-item of every visual row (offsetTop jumps to a new row)
     so CSS can left-pad its title/price. Banner rows, wrapping and "load more"
     all just work because we read live layout instead of counting children. */
  function initRowFirst() {
    const grid = document.getElementById('jjg-grid-' + SID);
    if (!grid) return;
    const FIRST = 'jjg-rowfirst';
    const LAST  = 'jjg-rowlast';
    function mark() {
      const items = Array.from(grid.querySelectorAll('.jjg-item'));
      items.forEach((it, i) => {
        const top  = it.offsetTop;
        const prev = items[i - 1];
        const next = items[i + 1];
        it.classList.toggle(FIRST, !prev || top > prev.offsetTop + 5);   // first of its row
        it.classList.toggle(LAST,  !next || next.offsetTop > top + 5);   // last of its row
      });
    }
    mark();
    window.addEventListener('load', mark, { once: true });
    let raf = 0;
    window.addEventListener('resize', () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(mark);
    }, { passive: true });
    new MutationObserver(mark).observe(grid, { childList: true });
  }

  function initMobileFeed() {
    const wrap = document.querySelector('.jjg-wrap');
    const grid = document.getElementById('jjg-grid-' + SID);
    if (!wrap || !grid) return;

    const panel = document.createElement('div');
    panel.className = 'jjg-mfeed';
    const slides = [];
    let slideH = 0;

    // The panel must be OPAQUE, otherwise the page sections behind the fixed panel
    // (page title, counter, banners) bleed through and look like static overlays.
    // We reproduce the header's resting tone exactly: 50% white over the same page
    // background the header itself sits on — so it reads identical to the header.
    let baseBg = getComputedStyle(document.body).backgroundColor;
    if (!baseBg || baseBg === 'rgba(0, 0, 0, 0)' || baseBg === 'transparent') {
      baseBg = WRAP.dataset.jjgBg || '#ffffff';
    }

    // VARIANT TOGGLE — true: card-stack "deck" (removed from the UX). false: the
    // explore-all parallax lookbook (active): one product per screen, photo drifts,
    // tap opens the product modal. The stacked branches below are dead while false.
    const STACKED = false;

    // The panel sits below the sticky header, exactly like the gallery lookbook.
    function headerH() {
      const h = document.querySelector('.shopify-section-group-header-group');
      return h ? Math.round(h.getBoundingClientRect().height) : 0;
    }
    // Card height = the panel's own laid-out height (down to the layout-viewport
    // bottom, behind the iOS toolbar) — EXACTLY like explore-all's `panel.clientHeight`,
    // so the cover+scale photo renders at the same size. (An earlier version used
    // visualViewport.height − headerH to dodge the toolbar, but that made the slide —
    // and thus the 0.72-scaled photo — shorter than explore-all's; the two feeds must
    // match.) Falls back before the panel is in the DOM.
    function measureSlideH() {
      return panel.clientHeight || Math.max(320, Math.round(window.innerHeight - headerH()));
    }
    // Scroll-critical styles are set INLINE from JS so a stale CSS cache can never
    // leave overflow:hidden / touch-action:none behind. Height in px, not svh/dvh.
    function stylePanel() {
      const top = headerH();
      // Opaque header-tone background (see baseBg above). No backdrop-filter — the
      // panel is opaque so there is nothing behind it to blur, and the blur was what
      // let the sections behind it show through as ghostly static shapes.
      panel.style.cssText =
        'position:fixed;left:0;right:0;top:' + top + 'px;bottom:0;z-index:5;' +
        'overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;' +
        // 'manipulation', NOT 'pan-y': touch-action INTERSECTS down the tree, so a
        // pan-y panel forbids horizontal panning in every descendant no matter what
        // they declare — the image strip inside each slide could never be swiped.
        // manipulation permits both axes and still suppresses the double-tap zoom
        // delay, which is what pan-y was really buying here.
        'touch-action:manipulation;overscroll-behavior:contain;' +
        'scroll-snap-type:y mandatory;background-color:' + baseBg + ';' +
        'background-image:linear-gradient(rgba(255,255,255,0.5),rgba(255,255,255,0.5));';
      slideH = measureSlideH();
    }
    stylePanel();

    // The grid img loaded a 50vw variant; ask the CDN for one sized to THIS device:
    // ~92% of screen width × devicePixelRatio, rounded up to 100s and capped. Bigger
    // costs decode time (janky reveal on cheap phones); smaller blurs on 3x screens.
    const IMG_W = (function () {
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const w = Math.min((window.screen && window.screen.width) || 9999, window.innerWidth || 390);
      return Math.max(600, Math.min(1600, Math.ceil((w * 0.92 * dpr) / 100) * 100));
    })();
    function imgHiRes(img) {
      if (!img) return '';
      const src = img.currentSrc || img.src || '';
      return src.replace(/width=\d+/, 'width=' + IMG_W);
    }

    /* — Product modal (explore-all bottom-sheet): a body-level overlay with an image
       carousel, description and product/wishlist buttons. Ported from the interactive
       gallery so both feeds behave identically. Uses the .ig-modal-* CSS above. — */
    let modalEl = null, modalOpen = false;

    // "SEK 16 000" / "16,000.00 kr" → "SEK 16 000": keep the integer part, drop the
    // trailing decimals, regroup thousands with a no-break space.
    function formatMobilePrice(raw) {
      if (!raw) return '';
      let n = raw.replace(/[^\d.,\s]/g, '').trim();   // drop currency letters/symbols
      n = n.replace(/[.,]\d{2}\s*$/, '');             // drop trailing .00 / ,00
      n = n.replace(/[^\d]/g, '');                     // digits only
      if (!n) return raw;
      // Thousands separator is a NO-BREAK space (U+00A0), not a plain one: a regular
      // space would let "6 753" break across two lines at the end of the caption row.
      // U+00A0 over the narrower U+202F because it's certain to exist in every font
      // the slide can fall back to — a missing glyph would render as tofu.
      n = n.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return 'SEK ' + n;
    }

    function closeModal() {
      modalOpen = false;
      if (!modalEl) return;
      const inner = modalEl.querySelector('.ig-modal');
      const el = modalEl;
      el.classList.remove('open');
      if (inner) {
        inner.classList.remove('open');
        inner.addEventListener('transitionend', function () { el.remove(); }, { once: true });
      } else {
        el.remove();
      }
      modalEl = null;
    }

    function openModal(d) {
      if (modalOpen) return;
      modalOpen = true;
      if (modalEl) modalEl.remove();

      const imgs = (d && d.images) || [];
      const el = document.createElement('div');
      el.className = 'ig-modal-backdrop';

      const slidesHtml = imgs.length
        ? imgs.map(function (src) { return '<div class="ig-modal-slide"><img src="' + src + '" alt="" loading="lazy"></div>'; }).join('')
        : '<div class="ig-modal-slide" style="background:#f4f4f4;"></div>';
      const arrowsHtml = imgs.length > 1
        ? '<button class="ig-modal-arrow prev" aria-label="Previous"><svg width="10" height="18" viewBox="0 0 10 18" fill="none"><path d="M9 1L1 9L9 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
          '<button class="ig-modal-arrow next" aria-label="Next"><svg width="10" height="18" viewBox="0 0 10 18" fill="none"><path d="M1 1L9 9L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>'
        : '';
      const dotsHtml = imgs.length > 1
        ? '<div class="ig-modal-dots">' + imgs.map(function (_, i) { return '<button class="ig-modal-dot' + (i === 0 ? ' active' : '') + '" data-index="' + i + '"></button>'; }).join('') + '</div>'
        : '';

      el.innerHTML =
        '<div class="ig-modal">' +
          '<button class="ig-modal-close" aria-label="Close">×</button>' +
          '<div class="ig-modal-images"><div class="ig-modal-track">' + slidesHtml + '</div>' + arrowsHtml + dotsHtml + '</div>' +
          '<h2 class="ig-modal-title">' + (d.title || '') + '</h2>' +
          '<div class="ig-modal-info">' +
            (d.desc ? '<div class="ig-modal-desc">' + d.desc + '</div>' : '') +
            (d.url ? '<a href="' + d.url + '" class="ig-modal-btn button">View product</a>' : '') +
            '<button class="ig-modal-btn ig-modal-wishlist-btn button" type="button" data-product-id="' + (d.pid || '') + '" data-product-handle="' + (d.handle || '') + '" aria-label="Spara produkt"><span>Wishlist</span></button>' +
          '</div>' +
        '</div>';

      document.body.appendChild(el);
      modalEl = el;

      // Wishlist toggle — shared jd_wishlist localStorage format, kept in sync with the
      // product-card hearts elsewhere on the page.
      const wishBtn = el.querySelector('.ig-modal-wishlist-btn');
      if (wishBtn && d.pid) {
        const getList = function () { try { return JSON.parse(localStorage.getItem('jd_wishlist') || '[]'); } catch (e) { return []; } };
        const matches = function (entry, id) { return typeof entry === 'string' ? entry === id : entry && entry.id === id; };
        if (getList().some(function (e2) { return matches(e2, d.pid); })) wishBtn.classList.add('active');
        wishBtn.addEventListener('click', function () {
          const list = getList();
          const idx = list.findIndex(function (e2) { return matches(e2, d.pid); });
          let added;
          if (idx === -1) { list.push({ id: d.pid, handle: d.handle }); added = true; }
          else { list.splice(idx, 1); added = false; }
          localStorage.setItem('jd_wishlist', JSON.stringify(list));
          wishBtn.classList.toggle('active', added);
          document.querySelectorAll('[data-product-id="' + d.pid + '"][class*="wishlist-btn"]').forEach(function (b) { b.classList.toggle('active', added); });
        });
      }

      requestAnimationFrame(function () { requestAnimationFrame(function () {
        el.classList.add('open');
        const inner = el.querySelector('.ig-modal'); if (inner) inner.classList.add('open');
        const imel = el.querySelector('.ig-modal-images'); if (imel) imel.style.opacity = '1';
      }); });

      if (imgs.length > 1) {
        let current = 0;
        const track = el.querySelector('.ig-modal-track');
        const dots = Array.from(el.querySelectorAll('.ig-modal-dot'));
        const goTo = function (i) {
          current = (i + imgs.length) % imgs.length;
          track.style.transform = 'translateX(-' + (current * 100) + '%)';
          dots.forEach(function (dt, idx) { dt.classList.toggle('active', idx === current); });
        };
        el.querySelector('.ig-modal-arrow.prev').addEventListener('click', function () { goTo(current - 1); });
        el.querySelector('.ig-modal-arrow.next').addEventListener('click', function () { goTo(current + 1); });
        dots.forEach(function (dt) { dt.addEventListener('click', function () { goTo(parseInt(dt.dataset.index, 10)); }); });
      }

      el.querySelector('.ig-modal-close').addEventListener('click', closeModal);
      el.addEventListener('click', function (e) { if (e.target === el) closeModal(); });
    }

    function buildSlide(item) {
      const url   = item.getAttribute('href') || '#';
      const img0  = item.querySelector('.jjg-slide img');
      const ltEl  = item.querySelector('.jjg-large-title');
      const ttEl  = item.querySelector('.jjg-title');
      const prEl  = item.querySelector('.jjg-price');
      // Two titles, like the desktop card: the decorative large_title metafield sits
      // above the full product title. Product title always exists; large_title is
      // optional. Guard against showing large_title twice if it equals the product title.
      const largeTxt = (ltEl && ltEl.textContent.trim()) || '';
      const titleTxt = (ttEl && ttEl.textContent.trim()) || largeTxt || '';
      const showLarge = largeTxt && largeTxt !== titleTxt;
      const priceTxt = prEl ? prEl.textContent.trim() : '';

      // Everything the modal needs, read off the grid card.
      const wishBtn = item.querySelector('.pc-wishlist-btn');
      const pid     = wishBtn ? wishBtn.getAttribute('data-product-id') : '';
      const handle  = wishBtn ? wishBtn.getAttribute('data-product-handle') : '';
      // Description lives in an escaped data attribute (NOT a nested node) — product
      // HTML can contain <a>, and an anchor inside the card's own <a> would break the DOM.
      const desc    = (item.getAttribute('data-jjg-desc') || '').trim();
      const images  = Array.from(item.querySelectorAll('.jjg-slide img')).map(function (im) {
        return (im.currentSrc || im.getAttribute('src') || '').replace(/width=\d+/, 'width=1200');
      }).filter(Boolean);

      // Slide keeps its <a href> (accessible + crawlable) but a genuine tap opens the
      // product modal instead of navigating — explore-all parity. A scroll gesture
      // cancels the click, so swiping never fires it.
      // All static styling lives in the CSS classes above (.jjg-m*); only
      // the per-instance / per-frame values are set inline here: the slide height, the
      // image src, and (later, in updateParallax) media transform + title opacity.
      const slide = document.createElement('a');
      slide.href = url;
      slide.className = 'jjg-mslide';
      slide.style.height = slideH + 'px';

      // Photo layer (parallax): a horizontal scroll-snap strip, one full-width frame
      // per product image. Each image is shown WHOLE in a 2:3 frame (aspect-ratio +
      // object-fit:contain) — uncropped, with air around it — instead of
      // cover-cropping to the slide shape.
      //
      // Axis handling is left entirely to the browser: the strip scrolls on x, the
      // panel on y, and the gesture's direction picks the winner. touch-action is
      // deliberately NOT set — pinning it to pan-x here would kill vertical scrolling
      // that starts on the photo, which is most of the screen.
      const media = document.createElement('div');
      media.className = 'jjg-mmedia';
      // Single-image products keep a plain (non-scrollable) strip, so nothing about
      // their behaviour changes.
      if (images.length > 1) media.classList.add('is-swipeable');

      const srcs = images.length ? images : [imgHiRes(img0)];
      let img = null;
      srcs.forEach(function (src, i) {
        const frame = document.createElement('div');
        frame.className = 'jjg-mframe';
        const im = document.createElement('img');
        im.className = 'jjg-mphoto';
        im.alt = titleTxt;
        im.decoding = 'async';
        // Frame 1 follows the old rule (first two slides eager, no top flash); the
        // rest only matter once the strip is actually swiped.
        // ORDER MATTERS: loading must be set BEFORE src. Once a fetch has started,
        // switching the attribute to lazy does not call it back.
        if (i > 0 || slides.length >= 2) im.loading = 'lazy';
        // src is deliberately NOT assigned here. buildSlide runs while the panel is
        // still detached, and an img outside the document is never "available for
        // lazy loading" — the attribute is ignored and the fetch fires immediately.
        // Assigning src at build time therefore pulled EVERY photo of EVERY product
        // at width=1200 in one burst (~90-145 images, ~800 MB-1.2 GB decoded), which
        // is what emptied the images and killed the tab on phones.
        // hydrateImages() assigns it once the panel is in the document.
        im.dataset.jjgSrc = src;
        if (i === 0) {
          img = im;
          // Until it decodes there is no naturalWidth, so the build-time measure falls
          // back to the full box and the caption sits at the band's bottom. Re-measure
          // BOTH the photo and the caption on arrival: photo-only left --jjg-ph-cap at
          // the pre-load value and the price stayed ~37px below where the caption
          // actually ended (visible on every lazy slide, i.e. everything past the first
          // two).
          im.addEventListener('load', function () { measureSlide(slide); }, { once: true });
        }
        frame.appendChild(im);
        media.appendChild(frame);
      });

      // Large title (custom.large_title metafield) — DECORATIVE text OVERLAID on the
      // product photo (its own absolutely-positioned block, independent from the product
      // title below the image). Only rendered when the metafield is set.
      // Deliberately a <div>, not a heading: it is styling, not document structure, and
      // it isn't always present — as a heading it outranked the actual product name and
      // left cards without it jumping a level. Heading order is h1 (page) → h2 (product).
      let largeInfo = null;
      if (showLarge) {
        largeInfo = document.createElement('div');
        largeInfo.className = 'jjg-mlarge-wrap';
        const lh = document.createElement('div');
        lh.className = 'jjg-mlarge';
        lh.textContent = largeTxt;
        largeInfo.appendChild(lh);
      }

      // Product title — sits BELOW the product photo. This is the real heading for the
      // slide (h2 under the page's h1), which is why the decorative large title above
      // is a plain div.
      const info = document.createElement('div');
      info.className = 'jjg-minfo-wrap';
      const h = document.createElement('h2');
      h.className = 'jjg-mtitle';
      h.textContent = titleTxt;
      info.appendChild(h);

      // Material (custom.title2) — the desktop card reveals this on hover, which no
      // touch device can do, so on mobile it is simply always visible as a second
      // caption line. Read off the grid card even though the media query hides it
      // there: textContent doesn't care about display:none.
      // It lives INSIDE .jjg-minfo-wrap so the measured caption height (--jjg-ph-cap)
      // includes it and the price drops below it on its own.
      const mtEl = item.querySelector('.jjg-hover-text');
      const matTxt = mtEl ? mtEl.textContent.trim() : '';
      if (matTxt) {
        const mat = document.createElement('p');
        mat.className = 'jjg-mmaterial';
        mat.textContent = matTxt;
        info.appendChild(mat);
      }

      // Price alone in the bottom-right, reformatted "SEK 16,000".
      const price = document.createElement('p');
      price.className = 'jjg-mprice';
      price.textContent = formatMobilePrice(priceTxt);

      slide.append(media, info, price);

      // Progress bar — only for products that actually have more than one image, so a
      // single-image product shows no affordance it can't honour. One thin segment per
      // photo, spanning the picture's width along its bottom edge.
      let dots = null;
      if (images.length > 1) {
        dots = document.createElement('div');
        dots.className = 'jjg-mbar';
        for (let i = 0; i < images.length; i++) {
          const d = document.createElement('span');
          d.className = 'jjg-mseg' + (i === 0 ? ' is-on' : '');
          dots.appendChild(d);
        }
        slide.appendChild(dots);
        // rAF-throttled: the strip fires scroll events far faster than the dot can
        // meaningfully change.
        let dRaf = null;
        media.addEventListener('scroll', function () {
          if (dRaf) return;
          dRaf = requestAnimationFrame(function () {
            dRaf = null;
            const w = media.clientWidth || 1;
            const at = Math.round(media.scrollLeft / w);
            for (let i = 0; i < dots.children.length; i++) {
              dots.children[i].classList.toggle('is-on', i === at);
            }
          });
        }, { passive: true });
      }

      // Large title is appended LAST so it paints ON TOP of the photo (appended earlier
      // in DOM order = lower z-index), otherwise the image covers its lower edge.
      if (largeInfo) slide.appendChild(largeInfo);
      slide._media     = media;
      slide._info      = info;
      slide._largeInfo = largeInfo;
      slide._img       = img;
      slide._modal = { url: url, pid: pid, handle: handle, title: titleTxt, desc: desc, images: images };
      // Mobile: tapping a product goes straight to its product page (no modal).
      // A swipe across the image strip must not count as a tap — a scroll gesture
      // doesn't always suppress the click, and opening a product by accident while
      // browsing its photos is the worst possible outcome here. Anything that moved
      // more than a finger's wobble is a swipe, not a tap.
      let downX = 0, downY = 0;
      slide.addEventListener('pointerdown', function (e) { downX = e.clientX; downY = e.clientY; }, { passive: true });
      slide.addEventListener('click', function (e) {
        e.preventDefault();
        const moved = Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY);
        if (moved > 10) return;
        if (url) window.location.href = url;
      });

      slides.push(slide);
      return slide;
    }

    /* Hand the stashed URLs to the <img>s now that they are in the document, so the
       loading="lazy" set in buildSlide is actually honoured and only the photos near
       the viewport are fetched. Must run AFTER the node is connected — that is the
       whole point. Safe to call twice: the attribute is removed as it is consumed. */
    function hydrateImages(root) {
      root.querySelectorAll('img.jjg-mphoto[data-jjg-src]').forEach(function (im) {
        const src = im.dataset.jjgSrc;
        delete im.dataset.jjgSrc;
        im.src = src;
      });
    }

    /* — Publish the photo's real top/bottom edge so the large title and the caption
         can hug it (see --jjg-ph-top / --jjg-ph-bot in the CSS).

         The photo is aspect-locked to 2:3 and capped at 94% width, so on most
         screens it does NOT fill its 80%-tall band — it sits centred inside it with
         slack above and below. Anchoring the texts to fixed slide percentages
         therefore left a gap that grew with the screen's aspect ratio.

         offsetTop/offsetHeight (not getBoundingClientRect) on purpose: they report
         layout position and ignore transforms, so a measurement taken mid-scroll
         isn't polluted by the parallax translate on _media. — */
    function measurePhoto(sl) {
      const im = sl._img, md = sl._media;
      if (!im || !md) return;
      const bw = im.offsetWidth, bh = im.offsetHeight;
      if (!bh) return;                      // not laid out yet — a later pass catches it
      let top = md.offsetTop + im.offsetTop;
      let h = bh;
      // The <img> BOX is not the picture. height:100% keeps the box as tall as the
      // whole band while max-width clamps its width, and object-fit:contain then
      // letterboxes the picture inside it — up to ~200px of transparent box above
      // and below the pixels. offsetHeight sees the box, so replay contain's maths
      // to get the painted rect (contain centres the remainder).
      const nw = im.naturalWidth, nh = im.naturalHeight;
      let w = bw;
      if (nw && nh) {
        const scale = Math.min(bw / nw, bh / nh);
        const painted = scale * nh;
        top += (bh - painted) / 2;
        h = painted;
        w = scale * nw;   // the progress bar spans exactly the picture, not the box
      }
      sl.style.setProperty('--jjg-ph-top', Math.round(top) + 'px');
      sl.style.setProperty('--jjg-ph-bot', Math.round(top + h) + 'px');
      sl.style.setProperty('--jjg-ph-w', Math.round(w) + 'px');
    }
    /* Where the caption block ends — the price hangs off this, because the title is
       uppercase and free to wrap and a two-line name would otherwise run through a
       price sitting at a fixed offset. Must be re-run whenever the photo vars move:
       the caption's own top is derived from --jjg-ph-bot, so a stale --jjg-ph-cap
       leaves the price stranded where the caption used to be.
       offsetHeight ignores the parallax transform on _info, so a mid-scroll measure
       is still correct. */
    function measureCaption(sl) {
      const inf = sl._info;
      if (!inf || !inf.offsetHeight) return;
      sl.style.setProperty('--jjg-ph-cap', (inf.offsetTop + inf.offsetHeight) + 'px');
    }
    function measureSlide(sl) { measurePhoto(sl); measureCaption(sl); }

    /* Two passes on purpose. Pass 1 writes every photo var, pass 2 reads the caption
       boxes those vars just moved — batched this way the browser does two layout
       flushes total instead of one per slide (50+ slides = 50+ forced reflows). */
    function measureAllPhotos() {
      slides.forEach(measurePhoto);
      slides.forEach(measureCaption);
    }

    // STACKED (reverse deck): cards are absolutely stacked inside a pinned "stage";
    // an invisible spacer below owns the scroll length + snap points.
    let stage = null, spacer = null;
    if (STACKED) {
      stage = document.createElement('div');
      stage.style.cssText = 'position:sticky;top:0;left:0;right:0;overflow:hidden;z-index:1;height:' + slideH + 'px;';
      spacer = document.createElement('div');
      spacer.style.cssText = 'position:relative;pointer-events:none;';
      panel._stage = stage; panel._spacer = spacer;
    }

    // Size the stage/cards and rebuild the spacer's snap markers (one per card 1..N-1;
    // card 0 rests on the firstMarker below). A trailing pad makes the last card
    // reachable even though the fixed panel runs taller than a card behind the iOS
    // toolbar. Called on load, resize and after pagination.
    function syncStack() {
      if (!STACKED || !stage) return;
      stage.style.height = slideH + 'px';
      slides.forEach(s => { s.style.height = slideH + 'px'; });
      // Top up markers instead of clearing: wiping the spacer momentarily shrinks
      // scrollHeight, which can clamp/jump the scroll position mid-measure.
      if (!spacer._pad) {
        spacer._pad = document.createElement('div');
        spacer._pad.style.cssText = 'pointer-events:none;';
        spacer.appendChild(spacer._pad);
        spacer._markers = 0;
      }
      const need = Math.max(0, slides.length - 1);
      while (spacer._markers < need) {
        const m = document.createElement('div');
        m.style.cssText = 'height:' + slideH + 'px;scroll-snap-align:start;pointer-events:none;';
        spacer.insertBefore(m, spacer._pad);
        spacer._markers++;
      }
      for (let ch = spacer.firstChild; ch; ch = ch.nextSibling) {
        if (ch !== spacer._pad) ch.style.height = slideH + 'px';
      }
      spacer._pad.style.height = Math.max(0, panel.clientHeight - slideH) + 'px';
    }
    // Intro height = where the stage begins (STACKED) or the first slide's flow top.
    function measureIntroH() {
      if (STACKED) return panel._stage ? panel._stage.offsetTop : 0;
      return (panel._intro && slides[0]) ? slides[0].offsetTop : 0;
    }

    // ── INTRO heading — intentionally OMITTED for explore-all parity ───────────
    // Earlier the feed opened with a compact heading block (page title + description
    // + "N Articles"). Being shorter than a full slide, its snap point made the first
    // swipe feel ragged. explore-all has no intro and opens straight on product 1 with
    // a clean 1:1 snap, which reads better — so the feed now does the same. With no
    // intro, `panel._intro` stays falsy → measureIntroH() returns 0 → introH = 0 and
    // the product indexing/parallax is offset by nothing. (The .jjg-mintro-* CSS is
    // now unused but left in place in case the intro is ever brought back.)

    // Build every currently-rendered product into the (still detached) panel first.
    grid.querySelectorAll('.jjg-item').forEach(item => {
      const sl = buildSlide(item);
      if (STACKED) { sl.style.zIndex = String(100000 - (slides.length - 1)); stage.appendChild(sl); }  // earlier card on top
      else panel.appendChild(sl);
    });
    if (!slides.length) return;   // nothing to show — leave the grid alone

    if (STACKED) {
      // firstMarker = card 0's snap rest (scrollTop = intro height). Final panel order
      // ends up: intro, firstMarker, stage (pinned), spacer.
      const firstMarker = document.createElement('div');
      firstMarker.style.cssText = 'height:1px;margin-bottom:-1px;scroll-snap-align:start;pointer-events:none;';
      panel.appendChild(firstMarker);
      panel.appendChild(stage);
      panel.appendChild(spacer);
    }

    // Swap grid → feed, then lock body scroll behind the fixed panel.
    wrap.style.display = 'none';

    // All sibling sections in #MainContent (page title, counter, banners) are hidden —
    // the feed opens straight on product 1 (no intro), and this page uses GSAP
    // ScrollSmoother, so those siblings would otherwise sit static above the fixed panel.
    // Hide — never move — theme nodes (moving trips the morph system and wipes the
    // injected panel).
    let introH = 0;
    const mySection = document.getElementById('shopify-section-' + SID);
    const main = document.getElementById('MainContent');
    if (main && mySection) {
      Array.from(main.children).forEach(el => {
        if (el !== mySection && el.classList.contains('shopify-section')) el.style.display = 'none';
      });
    }
    // The footer-group is rendered AFTER this section in the document, so at inline-
    // script time (during parsing) it isn't in the DOM yet — hiding it now is a no-op
    // and it floats up into view once the hidden page content collapses. Defer until
    // the DOM is parsed.
    function hideFooter() {
      document.querySelectorAll('.shopify-section-group-footer-group').forEach(f => { f.style.display = 'none'; });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hideFooter, { once: true });
    } else {
      hideFooter();
    }
    document.body.appendChild(panel);
    // Only now are the photos lazy-loadable — see hydrateImages().
    hydrateImages(panel);
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      slideH = measureSlideH();
      syncStack();
      introH = measureIntroH();
      slides.forEach(s => { s.style.height = slideH + 'px'; });
      measureAllPhotos();   // heights are final → photo edges are final
      updateParallax();
    });

    // A webfont swapping in re-wraps the uppercase title (GillSans is narrower than
    // the fallback), which changes the caption height the price hangs off. Measure
    // again once the fonts have settled.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { measureAllPhotos(); });
    }

    /* — Parallax: every slide is exactly the panel's height, so scrollTop/H is the
       fractional slide index — no getBoundingClientRect per frame. — */
    let raf = null;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; updateParallax(); maybePaginate(); });
    }
    function updateParallax() {
      const H = slides[0].offsetHeight || slideH || 1;
      // Product slides start below the intro, so subtract the intro height to get the
      // fractional PRODUCT-slide index (slides[0] centred when its top hits the panel).
      const s = (panel.scrollTop - introH) / H;
      const base = Math.floor(s);
      if (STACKED) {
        // Reverse deck: the top card (index floor(s)) slides UP and off to reveal the
        // still card beneath. Per-slide caches keep the frame cost ~O(1): only the
        // single mid-lift card has a CHANGING c, so only its transform is rewritten —
        // the loop over the rest is compare-and-skip. Nothing goes stale on momentum
        // scrolls because every card is still checked each frame.
        for (let i = 0; i < slides.length; i++) {
          const sl = slides[i];
          // c: 1 = lifted off (above base), 0 = at rest beneath, fraction only at base.
          const c = i < base ? 1 : (i > base ? 0 : Math.min(1, Math.max(0, s - i)));
          if (c !== sl._c) {
            sl._c = c;
            sl.style.transform = 'translate3d(0,' + (-c * H).toFixed(1) + 'px,0)';
          }
          // Promote only cards near the action — a static will-change on 50+ cards
          // pinned 50+ composited layers in GPU memory (jank on real phones).
          const near = i >= base - 1 && i <= base + 2;
          if (near !== sl._wc) {
            sl._wc = near;
            sl.style.willChange = near ? 'transform' : 'auto';
          }
          // Shadow ONLY while mid-lift, faded via sin so it never reads as a hard band
          // (a static shadow on every lifted card piled into a dark stripe up top).
          const shadow = (c > 0.01 && c < 0.99)
            ? '0 14px 30px rgba(0,0,0,' + (0.13 * Math.sin(c * Math.PI)).toFixed(3) + ')'
            : 'none';
          if (shadow !== sl._shadow) {
            sl._shadow = shadow;
            sl.style.boxShadow = shadow;
          }
        }
        // Load + pre-decode a window around the active card so the reveal never paints
        // a half-decoded image (decode() runs off the main thread's paint path).
        for (let i = base - 1; i <= base + 3; i++) {
          const sl = slides[i];
          if (!sl) continue;
          const im = sl._img;
          if (im && !im._loaded && im.dataset.src) {
            im._loaded = true;
            im.src = im.dataset.src;
            if (im.decode) im.decode().catch(function () {});
          }
        }
        return;
      }
      const P = 42;   // px the photo drifts within its taller frame
      for (let i = base - 1; i <= base + 1; i++) {
        const sl = slides[i];
        if (!sl) continue;
        const p = i - s;   // +below … 0 centred … −above
        // Photo drifts (subtle parallax); the Dymo title fades + eases as it leaves
        // centre — explore-all parity.
        sl._media.style.transform = 'translate3d(0,' + (-p * P).toFixed(1) + 'px,0)';
        sl._info.style.opacity    = Math.max(0, 1 - Math.abs(p) * 1.5).toFixed(3);
        sl._info.style.transform  = 'translateY(' + (p * 18).toFixed(1) + 'px)';
        // Large title is intentionally NOT animated — it stays static over the photo
        // (no fade/shift), so it never eases in as a grey→black overlay.
      }
    }
    panel.addEventListener('scroll', onScroll, { passive: true });

    /* — Feed-owned pagination: the grid's own infinite-scroll observer can't fire
       once the grid is display:none, so the feed pulls the next page itself. — */
    const sentinel = document.getElementById('jjg-sentinel-' + SID);
    let nextUrl = sentinel ? sentinel.dataset.next : null;
    let loading = false;
    function maybePaginate() {
      if (!nextUrl || loading) return;
      if (panel.scrollTop + panel.clientHeight * 2.5 < panel.scrollHeight) return;
      loading = true;
      fetch(nextUrl)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.getElementById('jjg-grid-' + SID);
          if (newGrid) {
            newGrid.querySelectorAll('.jjg-item').forEach(item => {
              if (!jdfKeep(item)) return;
              const sl = buildSlide(item);
              sl.style.height = slideH + 'px';
              if (STACKED) { sl.style.zIndex = String(100000 - (slides.length - 1)); stage.appendChild(sl); }
              else panel.appendChild(sl);
              hydrateImages(sl);   // now connected — see hydrateImages()
            });
            if (STACKED) syncStack();
            requestAnimationFrame(measureAllPhotos);   // new slides need a layout pass first
          }
          const ns = doc.getElementById('jjg-sentinel-' + SID);
          nextUrl = ns && ns.dataset.next ? ns.dataset.next : null;
          loading = false;
          // A filtered page can add few slides or none, so no scroll arrives to ask for the
          // next one. maybePaginate() checks the distance itself and stops when there is enough.
          if (JDF) maybePaginate();
        })
        .catch(() => { loading = false; });
    }
    // A filtered first page may hold only a slide or two — too short to scroll, so nothing
    // would ever ask for page 2. One frame later the panel has its height to measure against.
    if (JDF) requestAnimationFrame(maybePaginate);

    // Re-measure on EVERY resize, height included — explore-all parity. The iOS
    // toolbar sits expanded at load, so the first measurement is short; when the
    // toolbar collapses on the first scroll, the resize grows the slides (and thus the
    // cover+scale photo) to the full height, exactly like the interactive gallery.
    // (An earlier width-only guard kept the small load height forever — the photos
    // stayed visibly smaller than explore-all's. Debounced 160ms so it never churns
    // mid-scroll.)
    let rt = null;
    function remeasure() {
      clearTimeout(rt);
      rt = setTimeout(() => {
        stylePanel();
        syncStack();
        introH = measureIntroH();
        slides.forEach(s => { s.style.height = slideH + 'px'; });
        measureAllPhotos();   // slide height changed → the photo box did too
        updateParallax();
      }, 160);
    }
    window.addEventListener('resize', remeasure, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', remeasure, { passive: true });
  }

  /* ── INFINITE SCROLL ──────────────────────────── */
  const sentinel = document.getElementById('jjg-sentinel-' + SID);
  const gridEl = document.getElementById('jjg-grid-' + SID);
  let nextUrl = sentinel ? sentinel.dataset.next : null;
  let pageLoad = null;
  let observer = null;

  function wireItem(el) {
    const carousel = el.querySelector('[data-jjg-carousel]');
    if (carousel && !IS_MOBILE) initCarousel(carousel);
    el.querySelectorAll('.pc-wishlist-btn').forEach(initWishlistBtn);
  }

  // One page at a time; a second caller shares the request already in flight.
  function loadNext() {
    if (!nextUrl) return Promise.resolve(false);
    if (pageLoad) return pageLoad;
    pageLoad = fetch(nextUrl)
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.getElementById('jjg-grid-' + SID);
        if (newGrid) {
          Array.from(newGrid.children).forEach(el => {
            if (el.classList.contains('jjg-banner')) return;
            if (JDF_MASTER) JDF_MASTER.push(el);
            if (el.classList.contains('jjg-item') && !jdfKeep(el)) return;
            gridEl.appendChild(el);
            wireItem(el);
          });
        }
        const newSentinel = doc.getElementById('jjg-sentinel-' + SID);
        nextUrl = newSentinel && newSentinel.dataset.next ? newSentinel.dataset.next : null;
        if (nextUrl) {
          sentinel.dataset.next = nextUrl;
          // With a filter on, a page can add nothing and leave the sentinel in view — and an
          // observer only reports changes. Re-observing makes it report the current state.
          if (observer && JDF_LIVE && JDF_LIVE.active) { observer.unobserve(sentinel); observer.observe(sentinel); }
        } else {
          if (observer) observer.disconnect();
          sentinel.remove();
        }
      })
      .then(() => true, () => false)   // resolves with whether the page arrived
      .finally(() => { pageLoad = null; });
    return pageLoad;
  }

  if (sentinel) {
    observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadNext();
    }, { rootMargin: '200px' });
    observer.observe(sentinel);
  }

  /* ── IN-PLACE FILTERING (prototype) ───────────────
     jd-filter-client.js calls JDF.onChange() on every tick in the panel. The grid is
     rebuilt from JDF_MASTER — the same card elements, so their listeners survive — and
     the rest of the collection is fetched once so the filter reaches every product, not
     only the pages scrolled so far. Not on the mobile swipe feed: it is built once from
     the grid, and its page hides the bar the panel opens from. */
  if (JDF_LIVE && !IS_MOBILE) {
    const countBar = document.getElementById('product-count-total');
    const render = () => {
      const kids = JDF_LIVE.active
        ? JDF_MASTER.filter(el => el.classList.contains('jjg-item') && jdfKeep(el))
        : JDF_MASTER;
      gridEl.replaceChildren(...kids);
      kids.forEach(el => { if (el.classList.contains('jjg-item')) wireItem(el); });
      if (countBar) countBar.textContent = JDF_LIVE.active ? JDF_LIVE.total : (parseInt(WRAP.dataset.jjgTotal, 10) || 0);
    };
    // Page after page until none is left — stopping on a failed request rather than
    // retrying it forever; the scroll observer is still there to try again later.
    const loadAll = () => (nextUrl ? loadNext().then(ok => (ok ? loadAll() : null)) : Promise.resolve());
    JDF_LIVE.onChange = () => {
      render();
      if (nextUrl) loadAll().then(render);
    };
  }

  /* ── PRODUCT COUNT ────────────────────────────── */
  window.__productCount = (window.__productCount || 0) + (JDF ? JDF.total : (parseInt(WRAP.dataset.jjgTotal, 10) || 0));
  const countEl = document.getElementById('product-count-total');
  if (countEl) countEl.textContent = window.__productCount;

})();
